import { useState, useEffect, createContext, useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { selectUser, logout } from "features/userSlice";

import styles from "./styles.module.scss";
import Dashboard from "../dashboard/Dashboard";
import ConnectionPage from "../integrations/ConnectionPage";
import Header from "../header/Header";
import Footer from "pages/landing_page/Footer";
import CartPage from "../pages/cart/CartPage";
import SignUpPage from "../pages/signup_page/SignUpPage";
import ProductPage from "pages/product/ProductPage";
import CheckoutPage from "pages/checkout/CheckoutPage";
import ConfirmationPage from "pages/confirmation/ConfirmationPage";
import DirectCheckoutPage from "pages/directCheckout/DirectCheckoutPage";
import ReceiptPage from "pages/receipt/ReceiptPage";
import AutoShopPage from "pages/invoices/AutoShopPage";
import OrderHistoryPage from "pages/order-history/OrderHistoryPage";
import HomePage from "pages/home/HomePage";
import DirectVendors from "pages/directVendors/DirectVendors";
import DirectVendorPage from "pages/directVendorsPage/DirectVendorPage";
import { useUserStateContext, useDataContext } from "App";
import { sendGetCartsPayload } from "features/sendGetCartsPayload";
import DirectCheckoutHeader from "header/DirectCheckoutHeader";
import ErrorBoundary from "custom/error/ErrorBoundary";

const WebsocketContext = createContext();
const useWebsocketContext = () => useContext(WebsocketContext);
export { WebsocketContext, useWebsocketContext };

const renderComponent = (
  component,
  websocketContext,
  windowWidth,
  bannerHeight,
  headerHeight,
  sendJsonMessage,
  userCreds,
  user,
) => {
  switch (component) {
    case "dashboard":
      return (
        <WebsocketContext.Provider value={websocketContext}>
          <Dashboard
            windowWidth={windowWidth}
            bannerHeight={bannerHeight}
            headerHeight={headerHeight}
          />
        </WebsocketContext.Provider>
      );

    case "connection_page":
      return <ConnectionPage />;

    case "cart":
      return <CartPage />;

    case "signup_page":
      return <SignUpPage />;

    case "product":
      return <ProductPage sendJsonMessage={sendJsonMessage} />;

    case "checkout":
      return <CheckoutPage />;

    case "confirmation":
      return <ConfirmationPage />;

    case "directCheckout":
      return <DirectCheckoutPage />;

    case "receipt_page":
      return <ReceiptPage />;

    case "directVendors":
      return <DirectVendors />;

    case "directVendorPage":
      return <DirectVendorPage />;

    case "invoices":
      return <AutoShopPage />;

    case "order-history":
      return <OrderHistoryPage />;

    case "home":
      return <HomePage />;

    default:
      return null;
  }
};

const AuthedPage = ({ component, windowWidth, showHeader = true }) => {
  // for mobile shopping use
  const [bannerHeight, setBannerHeight] = useState(0);
  const [headerHeight, setHeaderHeight] = useState(0);
  const { userCreds } = useUserStateContext();
  const { connectedVendorCodes } = useDataContext();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);

  const connectWebSocket = (user_id) => {
    dispatch({ type: "WEBSOCKET_CONNECT", payload: { user_id: user_id } });
    dispatch({
      type: "DGI_API_WEBSOCKET_CONNECT",
      payload: { user_id: user_id },
    });
  };

  const connectGoWebSocket = (user_id) => {
    dispatch({
      type: "GO_API_WEBSOCKET_CONNECT",
      payload: { user_id: user_id },
    });
  };

  useEffect(() => {
    if (userCreds?.cognitoID) {
      connectWebSocket(userCreds.cognitoID);
      connectGoWebSocket(userCreds.cognitoID); // Add Go WebSocket connection
    }
    return () => {
      dispatch({ type: "WEBSOCKET_DISCONNECT" });
      dispatch({ type: "DGI_API_WEBSOCKET_DISCONNECT" });
      dispatch({ type: "GO_API_WEBSOCKET_DISCONNECT" }); // Add Go WebSocket disconnection
    };
  }, [userCreds.cognitoID]);

  // Add effect to clear Redux state on logout
  useEffect(() => {
    if (!userCreds?.cognitoID && user && Object.keys(user).length > 0) {
      dispatch(logout());
    }
  }, [userCreds?.cognitoID, user, dispatch]);

  const sendJsonMessage = (message) => {
    dispatch({ type: "WEBSOCKET_SEND_MESSAGE", payload: message });
  };

  const sendDgiApiJsonMessage = (message) => {
    dispatch({ type: "DGI_API_WEBSOCKET_SEND", payload: message });
  };

  const sendGoApiJsonMessage = (message) => {
    dispatch({ type: "GO_API_WEBSOCKET_SEND", payload: message });
  };

  const fetchInvoices = () => {
    // Generate a unique message ID for tracking response
    const message_id = `get_invoices-${Date.now()}`;

    // Send the request to the Go WebSocket server
    sendGoApiJsonMessage({
      action: "get_invoices",
      message_id: message_id,
    });
  };

  const websocketContext = useMemo(
    () => ({
      sendJsonMessage,
      sendDgiJsonMessage: sendDgiApiJsonMessage,
      sendGoJsonMessage: sendGoApiJsonMessage,
      fetchInvoices,
    }),
    [
      sendJsonMessage,
      sendDgiApiJsonMessage,
      sendGoApiJsonMessage,
      fetchInvoices,
    ],
  );

  useEffect(() => {
    // Fetch invoices if the user has printavo enabled in the new structure or has admin override
    if (
      user?.feature_access?.autopilot?.printavo?.enabled ||
      user?.feature_access?.autopilot?.printavo?.admin_override
    ) {
      fetchInvoices();
    }
  }, [
    user?.feature_access?.autopilot?.printavo?.enabled,
    user?.feature_access?.autopilot?.printavo?.admin_override,
  ]);

  useEffect(() => {
    sendGetCartsPayload(
      userCreds.cognitoID,
      sendDgiApiJsonMessage,
      dispatch,
      connectedVendorCodes,
      true,
    );
  }, [JSON.stringify(connectedVendorCodes), userCreds.cognitoID]);

  const childComponent = renderComponent(
    component,
    websocketContext,
    windowWidth,
    bannerHeight,
    headerHeight,
    sendJsonMessage,
    userCreds,
    user,
  );

  let renderHeader = null;
  if (component === "directCheckout") {
    renderHeader = <DirectCheckoutHeader />;
  } else {
    if (showHeader) {
      renderHeader = (
        <Header windowWidth={windowWidth} setHeaderHeight={setHeaderHeight} />
      );
    }
  }

  return (
    <>
      {renderHeader}
      <ErrorBoundary>
        <WebsocketContext.Provider value={websocketContext}>
          <main
            className={`${styles.mainContent} relative ${styles.isSearchFocused}`}
          >
            {childComponent}
          </main>
        </WebsocketContext.Provider>
      </ErrorBoundary>
      <Footer />
    </>
  );
};

export default AuthedPage;
